import axios, { isAxiosError } from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { auth } from '@/config/firebase';
import { API_BASE_URL } from '@/config/env';
import { ROUTES } from '@/app/router/routes';
import { logout } from '@/services/auth/authService';
import { analyticsService } from '@/services/analytics/analyticsService';
import { getAppLanguage } from '@/i18n/appLanguage';
import { useTenantStore } from '@/store/tenantStore';

/**
 * Axios API client — mirrors Flutter's HttpInterCeptor (Dio) + GenericHelpers.getHeaders().
 *
 * Interceptors handle:
 *   onRequest:  Firebase token (whenever a user is signed in), workflowId, language, WorkspaceId
 *               (fetched lazily when it isn't known yet, like getHeaders())
 *   onResponse: pass-through
 *   onError:    409 → /duplicateAccount, 401/403 → logout (revoke tokens + signOut) + /home
 *
 * Flutter only attaches HttpInterCeptor to the repositories' `dio` instances; calls made with
 * plain `http` or a bare `Dio()` bypass it. Their React counterparts pass skipErrorInterceptor.
 */

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Never send the Bearer token — mirrors Flutter getHeaders(isNoAuth: true) / headers.remove('Authorization'). */
    noAuth?: boolean;
    /** Leave 401/403/409 to the caller — mirrors Flutter calls made with plain http / Dio() (no HttpInterCeptor). */
    skipErrorInterceptor?: boolean;
    /**
     * Send only Authorization (when signed in) and, for requests with a body, Content-Type — no
     * workflowId, language or WorkspaceId. Mirrors Flutter calls made with plain `http` and just
     * those two headers (IAuthRepository.revokeRefreshTokens, ISavedJobsRepository.getSavedJobs).
     * Those calls also bypass HttpInterCeptor, so combine it with skipErrorInterceptor.
     */
    authHeaderOnly?: boolean;
  }
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Navigation helper (set by router) ────────────────────────────
type NavigateFn = (path: string, options?: { replace?: boolean }) => void;

let navigateFn: NavigateFn | null = null;

export function setNavigate(fn: NavigateFn): void {
  navigateFn = fn;
}

/**
 * True when the response interceptor has already acted on this error (navigated away),
 * so the caller should stop its own flow instead of handling it.
 */
export function isHandledByErrorInterceptor(error: unknown): boolean {
  if (!isAxiosError(error) || error.config?.skipErrorInterceptor) return false;
  const status = error.response?.status;
  return status === 409 || status === 401 || status === 403;
}

/** Mirrors Flutter pref 'workflowId' (SharedPreferences → persistent). */
function getWorkflowId(): string {
  let workflowId = localStorage.getItem('workflowId');
  if (!workflowId || !workflowId.startsWith('WFU_')) {
    workflowId = `WFU_${Date.now() * 1000}`;
    localStorage.setItem('workflowId', workflowId);
  }
  return workflowId;
}

const WORKSPACE_ID_PATH = 'v1/p/workspaceId?domain=';

/**
 * GET /v1/p/workspaceId?domain={hostname} — Flutter IWebHomeRepository.getWorkSpaceId().
 * The request interceptor sends it without any headers.
 */
export async function requestWorkspaceId(domain: string): Promise<string> {
  const res = await apiClient.get<string>(`${WORKSPACE_ID_PATH}${encodeURIComponent(domain)}`);
  return res.data;
}

let lazyWorkspaceId: Promise<string> | null = null;

/**
 * Mirrors the tail of Flutter GenericHelpers.getHeaders(): when no workspace id is known yet,
 * fetch it, keep it (CommonService.setWorkspaceId + APIConstants.TENANT_ID, which Flutter later
 * assigns to FirebaseAuth.tenantId) and send it. Concurrent requests share one lookup.
 */
function resolveWorkspaceId(): Promise<string> {
  const known = localStorage.getItem('workspaceId') || '';
  if (known) return Promise.resolve(known);
  lazyWorkspaceId ??= requestWorkspaceId(window.location.hostname)
    .then((id) => {
      if (id) {
        useTenantStore.getState().setWorkspaceId(id);
        auth.tenantId ??= id;
      }
      return id || '';
    })
    .catch(() => '')
    .finally(() => { lazyWorkspaceId = null; });
  return lazyWorkspaceId;
}

/** Firebase ID token — waits for the persisted session first, so page-load calls aren't anonymous (401). */
async function attachAuthToken(config: InternalAxiosRequestConfig): Promise<void> {
  await auth.authStateReady();
  const token = await auth.currentUser?.getIdToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
}

// ── Request Interceptor ──────────────────────────────────────────
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // Flutter's interceptor sends the workspaceId lookup without any headers
  if (config.url?.includes(WORKSPACE_ID_PATH)) {
    return config;
  }

  // Plain-http Flutter calls: Authorization (+ Content-Type when there is a body) only
  if (config.authHeaderOnly) {
    if (!config.noAuth) await attachAuthToken(config);
    return config;
  }

  // 1. Firebase auth token
  if (!config.noAuth) {
    await attachAuthToken(config);
  }

  // 2. workflowId
  config.headers.set('workflowId', getWorkflowId());

  // 3. Language (callers override it for tenant config lookups, e.g. 'tenantPreferences').
  //    Default: LocalDataService.getAppLanguage() — stored, else the browser language if supported.
  if (!config.headers.get('language')) {
    config.headers.set('language', getAppLanguage());
  }

  // 4. WorkspaceId (looked up first when it isn't known yet)
  const workspaceId = await resolveWorkspaceId();
  if (workspaceId) {
    config.headers.set('WorkspaceId', workspaceId);
  }

  // Content-Type: the instance default (application/json) goes out on every request with a body.
  // Flutter also sends it on bodiless GETs; axios's adapters strip it when there is no body, and
  // the backend has nothing to parse there, so that difference is left alone.

  return config;
});

// ── Response Error Interceptor ───────────────────────────────────
let unauthorizedLogout: Promise<void> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.config?.skipErrorInterceptor) {
      return Promise.reject(error);
    }

    const status = error.response?.status;

    // 409 → duplicate account. Flutter HttpInterCeptor.onError waits 1 s, then
    // navigateTo(CoreRoute.duplicateAccount, isClearStack: true).
    if (status === 409) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      // NavigationService.navigateTo logs the screen view (no analyticsClass here)
      void analyticsService.logScreenView({ screenName: ROUTES.DUPLICATE_ACCOUNT });
      navigateFn?.(ROUTES.DUPLICATE_ACCOUNT, { replace: true });
      return Promise.reject(error);
    }

    // 401 / 403 → revoke tokens, sign out and go home — mirrors committed Flutter
    // HttpInterCeptor.onError: IAuthRepository.logout() + FirebaseAuth.signOut(), then
    // navigateTo(homeRoute, isClearStack: true). Concurrent failures share one logout.
    if (status === 403) {
      unauthorizedLogout ??= logout()
        .catch(() => {/* already signed out */})
        .finally(() => { unauthorizedLogout = null; });
      await unauthorizedLogout;
      void analyticsService.logScreenView({ screenName: ROUTES.HOME });
      navigateFn?.(ROUTES.HOME, { replace: true });
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export default apiClient;
